import { useDiffMerge } from '@Context/prompt-toolbox/DiffMergeContext';
import { useUtilityContext } from '@Context/prompt-toolbox/UtilityContext';
import { usePromptToolboxContext } from '@Context/prompt-toolbox/PromptToolboxContext';
import {
  ArrowsOutCardinal,
  ShareFat,
  SquaresFour,
  Trash,
  GitDiff,
} from '@phosphor-icons/react';
import { useEffect } from 'react';
import { parseDate } from '@Generator/history/GenColumns';
import { BounceLoader } from 'react-spinners';
import colors from '@Common/assets/colors';
import { Tooltip } from 'react-tooltip';
import ReactiveIcon from '@Common/ReactiveIcon';
import SharePrompt from '../modals/SharePrompt';
import Modal from '@Common/Modal';
import Markdown from 'react-markdown';

const PrevPrompt = () => {
  const { selectedPromptVersion, sharePrompt } = usePromptToolboxContext();
  const { diffParts, setOriginal, setEdited, showDiff, setShowDiff } =
    useDiffMerge();
  const { modals, loaders, setModal } = useUtilityContext();

  useEffect(() => {
    if (selectedPromptVersion) {
      setOriginal(selectedPromptVersion.version.prompt);
      setEdited(selectedPromptVersion.version.prompt);
    }
  }, [selectedPromptVersion, setOriginal, setEdited]);

  const handleShare = async () => {
    //call sharing context
    setModal('sharePrompt', false);
    await sharePrompt();
  };

  return (
    <div className="flex h-full w-full flex-col gap-y-3 rounded-lg border-2 border-gray-200 p-3">
      {!selectedPromptVersion && (
        <span className="flex h-full flex-col items-center justify-center text-neutral-300">
          <SquaresFour size={150} weight="bold" />
          <label className="text-xs font-medium tracking-wider text-neutral-500">
            Select a prompt first
          </label>
        </span>
      )}
      {selectedPromptVersion && (
        <>
          {/* DETAILS */}
          <div className="flex w-full min-w-64 flex-row items-center justify-between border-gray-600 bg-white font-inter">
            <div className="flex flex-col">
              <label className="text-sm font-semibold">
                {selectedPromptVersion.version.updatedBy ??
                  selectedPromptVersion.author}
              </label>
              <div className="flex flex-col text-xs">
                <label>
                  {parseDate(Number(selectedPromptVersion.version.timestamp))}
                </label>
              </div>
            </div>
            {loaders.deleteVersion || loaders.sharePrompt ? (
              <div className="flex flex-row items-center gap-x-2">
                <label className="animate-pulse text-sm tracking-wide">
                  {loaders.sharePrompt
                    ? 'Uploading Version'
                    : 'Deleting Version'}
                </label>
                <BounceLoader size={20} color={colors.brandWarning[500]} />
              </div>
            ) : (
              <span className="flex flex-row gap-x-1">
                {/* Toggle Diff Button with typed parameter */}
                {/*testing*/}
                <button data-testid="show-diff-button"
                  onClick={() => setShowDiff((prev: boolean) => !prev)}
                  data-tooltip-id="prev"
                  data-tooltip-content={
                    showDiff ? 'Show Original' : 'Show Diff'
                  }
                  data-tooltip-place="left"
                  className="p-2"
                >
                  <ReactiveIcon
                    Icon={GitDiff}
                    clicked={false}
                    className={showDiff ? 'text-primary-700' : 'text-gray-500'}
                  />
                </button>
                <button data-testid="share-button"
                  data-tooltip-id="prev"
                  data-tooltip-content="Share"
                  data-tooltip-place="left"
                  onClick={() => setModal('sharePrompt', true)}
                >
                  <ReactiveIcon
                    Icon={ShareFat}
                    color={colors.primary[700]}
                    clicked={false}
                  />
                </button>
                <button data-testid="delete-bin"
                  data-tooltip-id="prev"
                  data-tooltip-content="Delete"
                  data-tooltip-place="left"
                  onClick={() => setModal('deleteVersion', true)}
                >
                  <ReactiveIcon
                    Icon={Trash}
                    color={colors.brandRed[500]}
                    clicked={modals.deleteVersion}
                  />
                </button>
                <Tooltip
                  id="prev"
                  style={{
                    color: 'white',
                    background: colors.primary[700],
                    borderRadius: '0.5rem',
                  }}
                  className="font-medium"
                />
              </span>
            )}
          </div>

          {/* PROMPT */}
          <div className="group relative flex h-1/2 w-full flex-col border-2 border-gray-300">
            <div className="h-full w-full pr-1">
              <div className="relative h-full w-full overflow-hidden">
                <div className="custom-scroll absolute inset-0 h-full w-full overflow-auto whitespace-pre-wrap break-words p-4">
                  {showDiff
                    ? diffParts.map((part, index) => (
                        <span
                          key={index}
                          className={
                            part.added
                              ? 'hidden'
                              : part.removed
                                ? 'bg-red-300 line-through'
                                : ''
                          }
                        >
                          {part.value}
                        </span>
                      ))
                    : selectedPromptVersion.version.prompt}
                </div>
              </div>
            </div>

            <div className="absolute bottom-2 right-2 flex flex-col gap-y-2 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
              <button
                data-tooltip-id="expand-prev"
                data-tooltip-content="Expand"
                data-tooltip-place="left"
                className="translate-y-4 transform rounded-full bg-primary-700 p-2 transition-transform duration-300 hover:shadow-xl group-hover:translate-y-0"
                onClick={() => setModal('expandVersionPrompt', true)}
              >
                <ArrowsOutCardinal
                  size={20}
                  weight="bold"
                  className="text-white"
                />
              </button>
              <Tooltip
                id="expand-prev"
                style={{
                  color: 'white',
                  background: colors.primary[700],
                  borderRadius: '0.5rem',
                }}
                className="font-medium"
              />
            </div>
          </div>

          {/* OUTPUT */}
          <div className="group relative flex h-1/2 w-full">
            <div className="flex h-full w-full flex-grow border-2 border-gray-300 pr-1">
              <Markdown className="markdown animate-typing custom-scroll h-full w-full resize-none overflow-y-auto p-4 text-sm focus:outline-none">
                {selectedPromptVersion.version.gpt_result}
              </Markdown>
            </div>
            <div className="absolute bottom-2 right-2 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
              <button
                data-tooltip-id="expand-prev"
                data-tooltip-content="Expand"
                data-tooltip-place="left"
                className="translate-y-4 transform rounded-full bg-primary-700 p-2 transition-transform duration-300 hover:shadow-xl group-hover:translate-y-0"
                onClick={() => setModal('expandGPTResult', true)}
              >
                <ArrowsOutCardinal
                  size={20}
                  weight="bold"
                  className="text-white"
                />
              </button>
              <Tooltip
                id="expand-prev"
                style={{
                  color: 'white',
                  background: colors.primary[700],
                  borderRadius: '0.5rem',
                }}
                className="font-medium"
              />
            </div>
          </div>
          <Modal
            isOpen={modals.sharePrompt}
            onClose={() => setModal('sharePrompt', false)}
            title="Share Prompt"
          >
            <SharePrompt onShare={handleShare} />
          </Modal>
        </>
      )}
    </div>
  );
};

export default PrevPrompt;
